import { RAW_cssValue } from "@classdojo/web/nessie/stylingLib";
import { translate } from "@classdojo/web/utils/translate";
import { useState, useCallback, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import EditPeopleModal from "./EditPeopleModal";
import WhoseTurnCardContent from "./WhoseTurnCardContent";
import ToolkitAppScroller from "components/BeyondToolkit/ToolkitAppScroller";
import { IdeasPencilIcon } from "components/IdeasIcons";
import { Person } from "components/PersonListItem";
import ToolkitCard from "components/ToolkitCard";
import { ParentStudentsSlice, selectStudents } from "src/pods/parentStudents";
import { logParentToolkitEvent } from "src/utils/logEvent";
import { WHOSE_TURN } from "utils/beyondToolkitTools";
import { sendUsage } from "utils/freemium";

const rolling = "/static/media/rolling-loop.mp3";
const finish = "/static/media/finish.mp3";

function mapStateToProps(state: ParentStudentsSlice) {
  return {
    students: selectStudents(state),
  };
}

type WhoseTurnProps = {
  innerHeight?: string | number;
  students: Person[];
};

function WhoseTurn(props: WhoseTurnProps) {
  const [additionalPersons, setAdditionalPersons] = useState<Person[]>([]);
  const [deselectedIds, setDeselectedIds] = useState<string[]>([]);
  const [showPeopleModal, setShowPeopleModal] = useState(false);

  const allPersons = useMemo(() => {
    return props.students.concat(additionalPersons);
  }, [props.students, additionalPersons]);

  const filteredPersons = useMemo(() => {
    return allPersons.filter((p) => !deselectedIds.includes(p._id));
  }, [allPersons, deselectedIds]);

  const { title, color, imgSrc } = WHOSE_TURN();

  const cancelEditPeople = useCallback(() => {
    setShowPeopleModal(false);
  }, [setShowPeopleModal]);

  const completeEditPeople = useCallback(
    (newAdditionalPersons: Person[], newDeselectedIds: string[]) => {
      setShowPeopleModal(false);
      setAdditionalPersons([...additionalPersons, ...newAdditionalPersons]);
      setDeselectedIds(newDeselectedIds);
    },
    [setShowPeopleModal, setAdditionalPersons, additionalPersons, setDeselectedIds],
  );

  // bulk ignore existing useEffects
  // eslint-disable-next-line @web-monorepo/no-useEffect
  useEffect(() => {
    sendUsage("whoseTurn");
  }, []);

  if (showPeopleModal) {
    return (
      <EditPeopleModal
        innerHeight={props.innerHeight}
        onClose={cancelEditPeople}
        onComplete={completeEditPeople}
        persons={allPersons}
        initialDeselectedIds={deselectedIds}
      />
    );
  }

  return (
    <ToolkitAppScroller title={title} color={color} imgSrc={imgSrc}>
      <audio id="rolling-audio" src={rolling} />
      <audio id="finish-audio" src={finish} />
      <div
        sx={{
          display: "flex",
          flexGrow: 1,
          flexDirection: "column",
          position: "relative",
        }}
      >
        <ToolkitCard>
          <WhoseTurnCardContent persons={filteredPersons} />
        </ToolkitCard>
      </div>
      <div
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "dt_m",
          position: "relative",
        }}
      >
        <button
          data-name="whose_turn:ideas_pencil_icon"
          onClick={() => {
            logParentToolkitEvent({ eventName: `whose_turn.edit.tap` });
            setShowPeopleModal(true);
          }}
          sx={{
            color: "dt_content_accent",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "transparent",
            border: "none",
            outlineColor: "transparent",
          }}
        >
          <IdeasPencilIcon color="dt_content_accent" size="s" sx={{ marginRight: RAW_cssValue("5px") }} />
          {translate("dojo.beyond:toolkit.whose_turn.edit_people")}
        </button>
      </div>
    </ToolkitAppScroller>
  );
}

export default connect(mapStateToProps)(WhoseTurn);
